import { useReducer, useCallback, useRef, useEffect} from "react"
import dayjs from "dayjs"
import { calculateMinWidth } from "src/utils/table-render-functions"

const initialState = {
    loading: false,
    rows: [],
    table: [],
    alert: { open: false, message: "", type: "" },
}

function reducer(state, action) {
    switch (action.type) {
        case "SET_LOADING":
            return { ...state, loading: action.payload }
        case "SET_ROWS":
            return { ...state, rows: action.payload }
        case "SET_TABLE":
            return { ...state, table: action.payload }
        case "SET_ALERT":
            return { ...state, alert: { ...state.alert, ...action.payload } }
        case "CLOSE_ALERT":
            return { ...state, alert: { ...state.alert, open: false } }
        default:
            return state
    }
}

const ordenaPorDataReferencia = (arr, order = "asc") => {
    return [...arr].sort((a, b) => {
        const dateA = a?.DataReferencia
            ? new Date(a.DataReferencia)
            : new Date(0)
        const dateB = b?.DataReferencia
            ? new Date(b.DataReferencia)
            : new Date(0)
        return order === "desc" ? dateB - dateA : dateA - dateB
    })
}

export const useIndiceLiquidezTable = (
    emissaoService,
    cetipIsin,
    dadosEmpresa
) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    const loadedRef = useRef(cetipIsin)
    
    useEffect(() => {
        if (loadedRef.current === cetipIsin) return

        loadedRef.current = cetipIsin
    
        dispatch({ type: "SET_ROWS", payload: [] })
        dispatch({ type: "SET_TABLE", payload: [] })
        dispatch({ type: "CLOSE_ALERT" })
            
    }, [cetipIsin])

    const buildTable = useCallback(() => {
        dispatch({
            type: "SET_TABLE",
            payload: [
                {
                    cells: [
                        `CETIP: ${dadosEmpresa?.CETIP || ""}`,
                        `ISIN: ${dadosEmpresa?.ISIN || ""}`,
                    ],
                },
            ],
        })
    }, [dadosEmpresa])

    const loadTableRows = useCallback(async () => {
        buildTable()
        dispatch({ type: "SET_LOADING", payload: true })
        try {
            const response = await emissaoService.indiceLiquidez(
                "get",
                `?CETIP=${cetipIsin}`
            )
            const { results } = response.body
            if (!results || results.length === 0) {
                dispatch({
                    type: "SET_ALERT",
                    payload: {
                        open: true,
                        message: "Não há dados para o CETIP consultado.",
                        type: "warning",
                    },
                })
                dispatch({ type: "SET_ROWS", payload: [] })
            } else {
                dispatch({ type: "CLOSE_ALERT" })
                const rows = ordenaPorDataReferencia(results, "desc").map(
                    (obj, i) => ({ ...obj, id: i })
                )
                dispatch({ type: "SET_ROWS", payload: rows })
            }
        } catch {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Não há dados para o CETIP consultado.",
                    type: "warning",
                },
            })
            dispatch({ type: "SET_ROWS", payload: [] })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [emissaoService, cetipIsin, buildTable])

    const addRow = useCallback(async () => {
        let lastItem = state.rows[0] || {
            CETIP: cetipIsin,
            DataReferencia: dayjs().format("YYYY-MM-DD"),
            IndiceLiquidez: 0,
        }
        dispatch({ type: "SET_LOADING", payload: true })
        try {
            await emissaoService.indiceLiquidez("post", lastItem)
            await loadTableRows()
        } catch {
            dispatch({
                type: "SET_ALERT",
                payload: {
                    open: true,
                    message: "Erro ao adicionar novo registro.",
                    type: "error",
                },
            })
        } finally {
            dispatch({ type: "SET_LOADING", payload: false })
        }
    }, [state.rows, cetipIsin, emissaoService, loadTableRows])

    const deleteRow = useCallback(
        async (row) => {
            if (!row?.ID) return
            dispatch({ type: "SET_LOADING", payload: true })
            try {
                await emissaoService.indiceLiquidez("delete", row.ID)
                await loadTableRows()
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
        },
        [emissaoService, loadTableRows]
    )

    const updateRow = useCallback(
        async (row) => {
            if (!row?.ID) return
            dispatch({ type: "SET_LOADING", payload: true })
            try {
                await emissaoService.indiceLiquidez("patch", row.ID, row)
                const updatedRows = state.rows.map((r) =>
                    r.ID === row.ID ? { ...r, ...row } : r
                )
                dispatch({ type: "SET_ROWS", payload: updatedRows })
            } finally {
                dispatch({ type: "SET_LOADING", payload: false })
            }
            return row
        },
        [state.rows, emissaoService]
    )

    const setAlert = useCallback(
        (payload) => dispatch({ type: "SET_ALERT", payload }),
        []
    )
    const closeAlert = useCallback(() => dispatch({ type: "CLOSE_ALERT" }), [])

    return {
        state: {
            ...state,
            minWidthIndiceLiquidez: calculateMinWidth(
                state.rows,
                "IndiceLiquidez"
            ),
        },
        actions: {
            loadTableRows,
            addRow,
            deleteRow,
            updateRow,
            setAlert,
            closeAlert,
            setRows: (rows) => dispatch({ type: "SET_ROWS", payload: rows }),
        },
    }
}
